"use client"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import Logo from "../../assets/images/logo/hazer-logo.png"
import apiUrls from "../../config/api-urls"
import { apiRequest } from "../../services/api-client"
import tokenService from "../../services/token-service"

const LoginModal = ({ isOpen, onClose, onSuccess, onSwitchToSignup }) => {
  const [formData, setFormData] = useState({
    phoneNumber: "",
    password: "",
  })
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState("")

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
    setError("") // Clear error on input change
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError("")

    try {
      const response = await apiRequest("POST", apiUrls.auth.login, {
        phoneNumber: formData.phoneNumber,
        password: formData.password,
      })

      if (response?.data?.data?.user?.token) {
        tokenService.setToken(response.data.data.user.token)
        tokenService.setUserDetails(response.data.data.user)
        
        // Store user in localStorage for authentication check
        localStorage.setItem("authUser", JSON.stringify(response.data.data.user))
        
        // Close modal
        onClose && onClose()
        
        // Call success callback
        onSuccess && onSuccess()
        
        // Reset form
        setFormData({ phoneNumber: "", password: "" })
        
        // Navigate or reload
        setTimeout(() => {
          window.location.reload() // Reload to refresh user data
        }, 1500)
      }
    } catch (err) {
      console.error("Login failed:", err)
      setError(err.response?.data?.message || "Login failed. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex flex-col md:flex-row min-h-[500px]">
          {/* Left */}
          <div className="hidden md:flex md:w-1/2 flex-col items-center justify-center text-center p-6 bg-gradient-to-br from-[#ff9906] to-[#f79d2d] text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Welcome to Hazer Globals</h2>
            <p className="text-sm md:text-lg mb-6">We make it easy for everyone to access their account.</p>
            <div className="bg-white rounded-2xl mt-4">
              <img src={Logo} alt="Hazer Logo" className="mt-6 w-32 h-32 md:w-40 md:h-40 rounded-lg shadow-lg" />
            </div>
          </div>

          {/* Right */}
          <div className="w-full md:w-1/2 p-6 md:p-8 relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold"
              aria-label="Close"
            >
              ×
            </button>

            <div className="max-w-sm mx-auto">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Login</h2>

              {error && <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}

              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="tel"
                  name="phoneNumber"
                  placeholder="Phone Number"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff8c00] focus:border-transparent outline-none"
                  required
                />

                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff8c00] focus:border-transparent outline-none pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? "👁️" : "👁️‍🗨️"}
                  </button>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" />
                    <span className="text-gray-600">Remember me</span>
                  </label>
                  <a href="/forgot-password" className="text-[#ff8c00] hover:underline">
                    Forgot Password?
                  </a>
                </div>

                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#ff8c00] text-white py-3 rounded-lg font-semibold hover:bg-[#e67c00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Logging in..." : "Login"}
                  </button>

                  <button
                    type="button"
                    onClick={onSwitchToSignup}
                    className="w-full bg-white text-[#ff8c00] py-3 rounded-lg font-semibold border border-[#ff8c00] hover:bg-[#ff8c00] hover:text-white transition-colors"
                  >
                    Register
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginModal